import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LineString } from 'geojson';

import { ESlice } from '@enums/redux.enum';
import { EStyles } from '@enums/style.enum';
import type { ReservationStatus, StopType, TripStatus } from '@enums/trips.enum';

export type Driver = {
	id: number;
	name: string;
	firstName: string;
	lastName: string;
	isVirtual?: boolean;
	email?: string;
	phone?: string;
	company?: string;
	status?: string;
	location?: {
		lat: number;
		lng: number;
	};
	suppliersIds?: number[];
	currentVehicleId?: number;
};

export type Vehicle = {
	id: number;
	name: string;
	seats?: number;
	year?: number;
	make?: string;
	model?: string;
	color?: string;
	licensePlate?: string;
};

export type LatLng = {
	lat: number;
	lng: number;
};

export type LatLngCoordinates = {
	type: string;
	coordinates: LatLng[];
};

export interface ILocation extends LatLng {
	name: string;
	displayName?: string;
	type: string;
}

export type Reservation = {
	id: number;
	date: number;
	dateType: StopType;
	distance: number;
	duration: number;
	email: string;
	fromAddress: ILocation;
	passengerId: number;
	passengerLabel: string;
	phone: string;
	roundTrip: boolean;
	seats: number;
	status: ReservationStatus;
	tags: string[];
	toAddress: ILocation;
	type: string;
	driver?: Driver;
	vehicle?: Vehicle;
};

export type MinimalReservation = {
	id: number;
	date: number;
	dateType: StopType;
	seats: number;
	status: ReservationStatus;
};

export type Passenger = {
	id: number;
	name: string;
	stopType: StopType;
	phone: string;
	email?: string;
	reservation: Reservation;
};

export type TripStop = {
	id: string;
	address: ILocation;
	date: number;
	passengers: Passenger[];
};

export type TripPayload = {
	records: Trip[];
	flightNumber: string;
};

export type Trip = {
	id: number;
	date: number;
	status: TripStatus;
	driver?: Driver;
	vehicle?: Vehicle;
	seats: number;
	stops: TripStop[];
	distance: number;
	duration: number;
	flightNumber: string;
	airline: string;
	trackingUrl?: string;
	reservationsCount: number;
	from: ILocation;
	to: ILocation;
	endTripETA?: number;
	route?: LineString;
	reservations?: MinimalReservation[];
};

type InitialState = {
	data: Trip[];
	currentTrip?: Trip;
	flightNumber?: string;
	airline?: string;
};

const initialState: InitialState = {
	data: [],
	currentTrip: undefined,
	flightNumber: undefined,
	airline: undefined,
};

const tripsSlice = createSlice({
	name: ESlice.trips,
	initialState,
	reducers: {
		setTrips: (state, { payload }: PayloadAction<TripPayload>) => {
			state.data = payload.records;
			state.airline = payload.records[0]?.airline || EStyles.emptyQuote;
			state.flightNumber = payload.records[0]?.flightNumber || payload.flightNumber;
			state.currentTrip = undefined;
		},
		updateTrip: (state, { payload }: PayloadAction<Trip>) => {
			const index = state.data.findIndex((trip) => trip.id === payload.id);
			if (index > -1) {
				state.data[index] = payload;
			}
		},
		removeTrip: (state, { payload }: PayloadAction<Trip['id']>) => {
			state.data = state.data.filter((trip) => trip.id !== payload);
		},
		setCurrentTrip: (state, { payload }: PayloadAction<Trip>) => {
			state.currentTrip = payload;
		},
		resetCurrentTrip: (state) => {
			state.currentTrip = undefined;
		},
		updateCurrentTripStatus: (state, { payload }: PayloadAction<TripStatus>) => {
			if (state.currentTrip) {
				state.currentTrip.status = payload;
			}
		},
		addDriverTrackingUrl: (state, { payload }: PayloadAction<Trip['trackingUrl']>) => {
			if (state.currentTrip) {
				state.currentTrip.trackingUrl = payload;
			}
		},
		addDriverToTrip: (state, { payload }: PayloadAction<Driver>) => {
			if (state.currentTrip) {
				state.currentTrip.driver = { ...state.currentTrip.driver, ...payload };
			}
		},
		removeDriverFromTrip: (state) => {
			if (state.currentTrip) {
				state.currentTrip.driver = undefined;
			}
		},
		addVehicleToTrip: (state, { payload }: PayloadAction<Vehicle>) => {
			if (state.currentTrip) {
				state.currentTrip.vehicle = { ...state.currentTrip.vehicle, ...payload };
			}
		},
		removeVehicleFromTrip: (state) => {
			if (state.currentTrip) {
				state.currentTrip.vehicle = undefined;
			}
		},
		updateDriverVehicleTrip: (state, { payload }: PayloadAction<{ id: Trip['id']; driver?: Driver; vehicle?: Vehicle }>) => {
			const index = state.data.findIndex((trip) => trip.id === payload.id);
			if (index !== -1) {
				if (payload.driver) {
					state.data[index].driver = payload.driver;
				}
				if (payload.vehicle) {
					state.data[index].vehicle = payload.vehicle;
				}
			}
		},
		removeDriverFromTripsList: (state, { payload }: PayloadAction<{ id: Trip['id'] }>) => {
			const index = state.data.findIndex((trip) => trip.id === payload.id);
			if (index !== -1) {
				state.data[index].driver = undefined;
			}
		},
		removeVehicleFromTripsList: (state, { payload }: PayloadAction<{ id: Trip['id'] }>) => {
			const index = state.data.findIndex((trip) => trip.id === payload.id);
			if (index !== -1) {
				state.data[index].vehicle = undefined;
			}
		},
	},
});

export const {
	setTrips,
	setCurrentTrip,
	resetCurrentTrip,
	updateTrip,
	removeTrip,
	updateCurrentTripStatus,
	addDriverTrackingUrl,
	addDriverToTrip,
	removeDriverFromTrip,
	addVehicleToTrip,
	removeVehicleFromTrip,
	updateDriverVehicleTrip,
	removeDriverFromTripsList,
	removeVehicleFromTripsList,
} = tripsSlice.actions;

export default tripsSlice.reducer;
